import { createSlice, createAsyncThunk, current } from '@reduxjs/toolkit'
import { getData, postData, putData, getRemoteUrl, deleteData } from './request'
import { addCases } from './utils'
import { getLoginToken } from './store'
import { getCurrentSessionGeneration, sessionInvalidated } from './session'

function initialState() {
    return {
        needToRequest: {ftp:true, weight:true},
        records: {ftp:[], weight:[]},
        isLoading: {ftp:false, weight:false},
        requestError: undefined,
        sessionGeneration: 0,
    }
}

function setErrorState(state, { payload }) {
    if (payload && payload.sessionGeneration !== state.sessionGeneration) return
    state.requestError = (payload && payload.error) || undefined
}

function setUserMeasuresInternal(state, { payload }) {
    if (payload.sessionGeneration !== state.sessionGeneration) return
    state.records[payload.metric] = payload.records
    state.isLoading[payload.metric] = false
}

function setUserMeasuresLoading(state, action) {
    const metric = action.meta.arg
    if(!metric)
        return
    
    state.isLoading[metric] = true
    state.needToRequest[metric] = false
}

export function reqUserMeasures(metric) {
    const sessionGeneration = getCurrentSessionGeneration()
    const loginToken = getLoginToken()
    let params = {
        method: 'GET',
        headers: {
            'Authorization': `Bearer ${loginToken}`
        }
    }

    return getData(getRemoteUrl("users/me/measures/" + metric + "/history"), params)
        .then(json => ({ sessionGeneration, metric: metric, records: json.records }))
        .catch(err => {
            if (err && (err.status === 404 || err.code === 'NoData')) {
                return { sessionGeneration, metric: metric, records: [] }
            }

            throw err
        })
}

export function reqAddMeasure(metric, date, value) {
    const loginToken = getLoginToken()

    const data = {
        date: date,
        value: value
    }

    return postData(getRemoteUrl("users/me/measures/" + metric), data, loginToken)
}

export function reqChangeMeasureRecord(metric, recordId, newDate, newValue) {
    const data = {
        date: newDate,
        value: newValue
    }

    return putData(getRemoteUrl("users/me/measures/" + metric + "/" + recordId), data, getLoginToken())
}

export function reqDeleteMeasureRecord(metric, recordId) {
    return deleteData(getRemoteUrl("users/me/measures/" + metric + "/" + recordId), null, getLoginToken())
}


export const asyncReqUserMeasures = createAsyncThunk(
    'asyncReqUserMeasures',
    async (metric, { rejectWithValue }) => {
        try {
            return await reqUserMeasures(metric)
        } catch (error) {
            return rejectWithValue({ sessionGeneration: getCurrentSessionGeneration(), error })
        }
    }
)

export const usermeasuresSlice = createSlice({
    name: 'usermeasures',
    initialState: initialState(),
    reducers: {
        setUserMeasures(state, { payload }) {
            const oldState = current(state)
            let newState = {
                ...oldState,
                records:{}
            }

            for(let m of Object.keys(oldState.records)) {
                if (m === payload.metric)
                    newState.records[m] = payload.records
                else
                    newState.records[m] = oldState.records[m]
            }

            return newState
        },
        updateMeasureRecord(state, {payload}) {
            const oldState = current(state)
            const records = oldState.records[payload.metric].map(record => {
                if (record.id !== payload.id) {
                    return record
                }
    
                return {
                    ...record,
                    date:payload.date,
                    value:payload.value
                }
            })

            let newState = {
                ...oldState,
                records:{}
            }

            for(let m of Object.keys(oldState.records)) {
                if (m === payload.metric)
                    newState.records[m] = records
                else
                    newState.records[m] = oldState.records[m]
            }
            return newState
        }
    },
    extraReducers: builder => {
        addCases(builder, asyncReqUserMeasures, setUserMeasuresInternal, setUserMeasuresLoading, setErrorState)
        builder.addCase(sessionInvalidated, (state, action) => ({
            ...initialState(),
            sessionGeneration: action.payload,
        }))
    }
})

export const { setUserMeasures, updateMeasureRecord } = usermeasuresSlice.actions